import { useEffect, useState } from "react";
import { HeaderSection } from "./header-section"
import { SearchBar } from "./search-bar"
import { TemplateList } from "./template-list"
import { AdminVideoTemplateModal } from "./admin-video-template-modal";
import { agentCompanyAssetHooks } from "@/manager";
import { AdminVideoTemplateListItemData } from "@/services/admin-video-template-list";
import { AdminPostTemplateModal } from "./admin-post-template-modal";
import { AdminPostTemplateListItemData } from "@/services/admin-post-template-list";
import { AdminArticleTemplateModal } from "./admin-article-template-modal";
import { AdminTemplateListItemData } from "@/services/admin-article-template-list";
import { cn } from "@/lib/utils";
import { AssetsToolBar } from "../common/assets-tool-bar";
import { useTranslation } from "react-i18next";
import { useCancelSelectOnBlankClick } from "@/hooks/use-cancel-select-on-blank-click";

/** 企业资产库 */
export const CompanyAsset = () => {
  const { t } = useTranslation();
  // 模板选择弹窗
  const [showChooseTemplateModal, setShowChooseTemplateModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showToolBar, setShowToolBar] = useState(false);

  const {
    templates,
    loading,
    searchFormData,
    search,
    changePage,
    totalTemplateCount,
    resetAndSearch,
    changeSearchForm,
  } = agentCompanyAssetHooks.useTemplatesData();

  const { loading: addLoading, handleAddTemplate } = agentCompanyAssetHooks.useTemplateAdd();
  const { loading: toggleLoading, handleToggleTemplateStatus } = agentCompanyAssetHooks.useTemplateToggleStatus();
  const { loading: deleteLoading, handleDeleteTemplate } = agentCompanyAssetHooks.useTemplateDelete();

  // 保存短视频模板（后台视频模板列表；入参与原风格弹窗一致）
  const handleSaveStyleTemplate = async (selectedList: AdminVideoTemplateListItemData[]) => {
    try {
      await handleAddTemplate(selectedList.map(item => ({
        templateName: item.name,
        templateId: item.styleId,
        coverUrl: item.coverUrl,
        coverVideo: item.demoUrl || '',
        scene: item.scene,
      })));
    } catch (error) {
      console.error('error:', error);
    }
  }

  // 保存图文模板（后台图文模板列表；入参与原飞书弹窗一致）
  const handleSavePostTemplate = async (selectedList: AdminPostTemplateListItemData[]) => {
    try {
      await handleAddTemplate(selectedList.map(item => ({
        templateName: item.templateName,
        templateId: item.templateId,
        coverUrl: item.coverUrl,
      })));
    } catch (error) {
      console.error('error:', error);
    }
  }

  // 保存文章模板（后台文章模板列表；入参与原飞书弹窗一致）
  const handleSaveArticleTemplate = async (selectedList: AdminTemplateListItemData[]) => {
    try {
      await handleAddTemplate(selectedList.map(item => ({
        templateName: item.templateName,
        templateId: item.templateId,
        coverUrl: item.coverUrl,
      })));
    } catch (error) {
      console.error('error:', error);
    }
  }

  // 切换模板状态
  const handleToggleTemplate = async (id: number) => {
    try {
      await handleToggleTemplateStatus(id);
      search();
    } catch (error) {
      console.error('error:', error);
    }
  }

  // 处理单个选中
  const handleSelected = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelectedIds(prev => {
      const nextSet = new Set(prev);
      if (checked) {
        nextSet.add(id);
      } else {
        nextSet.delete(id);
      }
      return Array.from(nextSet);
    });
  }

  // 处理取消选择/退出多选模式
  const handleCancelSelect = () => {
    setSelectedIds([]);
    setShowToolBar(false);
  }

  // 处理全选/反选所有文件夹
  const handleSelectedAll = (checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        return templates.map(item => item.id);
      }
      return [];
    });
  }

  // 批量删除
  const handleBatchDelete = async () => {
    try {
      await handleDeleteTemplate(selectedIds);
      handleCancelSelect();
    } catch (error) {
      console.error('error:', error);
    }
  }

  useEffect(() => {
    setShowToolBar(selectedIds.length > 0);
  }, [selectedIds]);

  // 点击空白区域取消选择/退出多选模式
  const cancelSelectOnBlankClick = useCancelSelectOnBlankClick({
    enabled: selectedIds.length > 0,
    // 弹窗打开时阻塞
    onClick: handleCancelSelect,
  });

  return (
    <div className='w-full h-full flex flex-col overflow-hidden'
      {...cancelSelectOnBlankClick}
    >
      <HeaderSection />
      <SearchBar
        searchFormData={searchFormData}
        presetTemplateCount={templates?.length}
        totalTemplateCount={totalTemplateCount}
        changeSearchForm={changeSearchForm}
        onSearch={search}
        onReset={resetAndSearch}
        onAddClick={() => setShowChooseTemplateModal(true)}
        onBatchClick={() => setShowToolBar(!showToolBar)}
      />
      <TemplateList
        loading={loading}
        templates={templates}
        type={searchFormData.type}
        toggleLoading={toggleLoading}
        deleteLoading={deleteLoading}
        onToggleStatus={handleToggleTemplate}
        onDelete={handleDeleteTemplate}
        onSelected={handleSelected}
        selectedIds={selectedIds}
        openSelected={showToolBar}
      />
      {/* 短视频模板选择弹窗（后台视频模板） */}
      {searchFormData.type === 1 && showChooseTemplateModal && (
        <AdminVideoTemplateModal
          multiple
          showSelectAll
          open={showChooseTemplateModal}
          onOpenChange={setShowChooseTemplateModal}
          onSave={handleSaveStyleTemplate}
          saving={addLoading}
        />
      )}
      {/* 图文模板选择弹窗 */}
      {searchFormData.type === 4 && showChooseTemplateModal && (
        <AdminPostTemplateModal
          open={showChooseTemplateModal}
          onOpenChange={setShowChooseTemplateModal}
          onSave={handleSavePostTemplate}
          saving={addLoading}
        />
      )}
      {/* 文章模板选择弹窗（后台文章模板） */}
      {searchFormData.type === 5 && showChooseTemplateModal && (
        <AdminArticleTemplateModal
          open={showChooseTemplateModal}
          onOpenChange={setShowChooseTemplateModal}
          onSave={handleSaveArticleTemplate}
          saving={addLoading}
        />
      )}
      {/* 工具栏 */}
      <div
        className={cn(
          "transition-all duration-300 ease-in-out",
          (selectedIds.length > 0 || showToolBar) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <AssetsToolBar
          onClose={handleCancelSelect}
          isSelectedAll={selectedIds.length === templates?.length}
          selectedAmount={selectedIds.length}
          onSelectedAll={handleSelectedAll}
          onDelete={handleBatchDelete}
          deleteTitle={t('agent_dashboard.company_asset.delete')}
          deleteLoading={deleteLoading}
          deleteDesc={
            searchFormData?.type === 1 ?
              t('agent_dashboard.company_asset.delete_desc', { name: templates.filter(item => selectedIds.includes(item.id)).map(item => item.templateName).join(', ') })
              : t('agent_dashboard.company_asset.delete_desc2', { name: templates.filter(item => selectedIds.includes(item.id)).map(item => item.templateName).join(', ') })
          }
        />
      </div>
    </div>
  )
}

